import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  ChevronDown,
  Code,
  BarChart3,
  Smartphone,
  Brain,
  Zap,
  ShoppingCart,
  Palette,
  Users,
  Package,
  Award,
  Target,
  TrendingUp,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Card, CardContent } from "../ui/Card";
import "./MHeader.css";

const services = [
  {
    name: "Digital Marketing",
    slug: "digital-marketing-service",
    icon: BarChart3,
    description:
      "Boost your online presence with data-driven marketing strategies.",
    gradient: "linear-gradient(90deg, #ec4899, #f87171)",
  },
  {
    name: "Web Development",
    slug: "web-development-service",
    icon: Code,
    description:
      "Custom websites built with modern technologies and best practices.",
    gradient: "linear-gradient(90deg, #3b82f6, #06b6d4)",
  },
  {
    name: "Mobile App Development",
    slug: "mobile-app-development-service",
    icon: Smartphone,
    description: "Native and cross-platform mobile apps for iOS and Android.",
    gradient: "linear-gradient(90deg, #10b981, #34d399)",
  },
  {
    name: "AI Development",
    slug: "ai-development-service",
    icon: Brain,
    description: "Intelligent solutions powered by machine learning and AI.",
    gradient: "linear-gradient(90deg, #a78bfa, #6366f1)",
  },
  {
    name: "IT Consulting & Strategy",
    slug: "it-consulting-service",
    icon: Zap,
    description: "Strategic technology consulting to optimize your operations.",
    gradient: "linear-gradient(90deg, #facc15, #f97316)",
  },
  {
    name: "E-Commerce Development",
    slug: "ecommerce-development-service",
    icon: ShoppingCart,
    description: "Comprehensive e-commerce solutions for online businesses.",
    gradient: "linear-gradient(90deg, #14b8a6, #3b82f6)",
  },
  {
    name: "UI/UX Development",
    slug: "ui-ux-development-service",
    icon: Palette,
    description:
      "User-centered design solutions for engaging digital experiences.",
    gradient: "linear-gradient(90deg, #f43f5e, #ec4899)",
  },
  {
    name: "Hire Dedicated Teams",
    slug: "hire-dedicated-service",
    icon: Users,
    description: "Skilled dedicated development teams for your projects.",
    gradient: "linear-gradient(90deg, #06b6d4, #3b82f6)",
  },
  {
    name: "Amazon Account Management",
    slug: "amazon-account-management",
    icon: Package,
    description: "Complete Amazon seller account management services.",
    gradient: "linear-gradient(90deg, #f97316, #f59e0b)",
  },
  {
    name: "Amazon Brand Registry",
    slug: "amazon-brand-registry",
    icon: Award,
    description: "Professional brand registry services for Amazon sellers.",
    gradient: "linear-gradient(90deg, #6366f1, #a78bfa)",
  },
  {
    name: "Advertising & PPC",
    slug: "advertising-ppc",
    icon: Target,
    description: "Strategic PPC campaigns that deliver measurable results.",
    gradient: "linear-gradient(90deg, #ef4444, #f43f5e)",
  },
  {
    name: "Amazon Enhanced Brand Content",
    slug: "amazon-enhanced-brand-content",
    icon: TrendingUp,
    description: "Professional enhanced brand content for Amazon listings.",
    gradient: "linear-gradient(90deg, #34d399, #14b8a6)",
  },
];

export default function Header() {
  const location = useLocation();
  const currentPath = location.pathname.replace(/^\/+/, "");
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [hoveredService, setHoveredService] = useState(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "Home", path: "" },
    { name: "About", path: "about" },
    { name: "Services", path: "services", hasDropdown: true },
    { name: "Portfolio", path: "portfolio" },
    { name: "Blog", path: "blog" },
    { name: "Contact", path: "contact" },
  ];

  return (
    <motion.header
      className={`fixed-top transition-all w-100 ${
        isScrolled
          ? "bg-white bg-opacity-90 shadow-sm border-bottom"
          : "bg-transparent"
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      style={isScrolled ? { backdropFilter: "blur(15px)" } : {}}
    >
      <div
        className="container px-3 px-lg-5 py-3"
        onMouseLeave={() => setIsServicesOpen(false)}
      >
        <div className="d-flex align-items-center justify-content-between">
          {/* Logo */}
          <Link
            to="/"
            className="d-flex align-items-center text-decoration-none"
          >
            <img
              src="/assets/img/logo/Meta_fox.png"
              alt="logo"
              style={{ maxWidth: "180px" }}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="d-none d-lg-flex align-items-center position-relative">
            {navItems.map((item) => {
              if (item.hasDropdown) return null;
              return (
                <div key={item.name} className="position-relative mx-3">
                  <Link
                    to={`/${item.path}`}
                    className={`text-body fs-6 py-2 text-decoration-none ${
                      currentPath === item.path ? "text-primary" : ""
                    }`}
                  >
                    {item.name}
                    {currentPath === item.path && (
                      <motion.div
                        className="position-absolute start-0 end-0 bottom-0"
                        style={{
                          height: "2px",
                          background:
                            "linear-gradient(90deg, #3b82f6, #1e40af)",
                        }}
                        layoutId="activeTab"
                      />
                    )}
                  </Link>
                </div>
              );
            })}

            {/* Services Button (separate) */}
            <div
              className="position-relative mx-3"
              onMouseEnter={() => setIsServicesOpen(true)}
            >
              <button
                type="button"
                className="btn btn-link text-body fs-6 py-2 text-decoration-none"
              >
                Services
                <motion.span
                  animate={{ rotate: isServicesOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="ms-1 d-inline-block"
                >
                  <ChevronDown className="icon-sm" />
                </motion.span>
              </button>
            </div>
          </nav>

          {/* CTA */}
          <div className="d-none d-lg-flex align-items-center">
            <Link
              to="/contact"
              className="btn btn-primary px-4 py-2 rounded-pill shadow-lg"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Button */}
          <button
            className="btn btn-outline-primary d-lg-none p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Full-Width Services Dropdown (outside nav map) */}
        <AnimatePresence>
          {isServicesOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="position-absolute start-0 top-100 bg-white border-top shadow"
              style={{ left: "0", width: "100vw", zIndex: 999 }}
            >
              <div className="container px-3 px-lg-5 py-4">
                <div className="row g-3">
                  {services.map((service) => {
                    const Icon = service.icon;
                    return (
                      <motion.div
                        key={service.slug}
                        onMouseEnter={() => setHoveredService(service.slug)}
                        onMouseLeave={() => setHoveredService(null)}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="col-12 col-md-6 col-lg-4 col-xl-3"
                      >
                        <Link
                          to={`/${service.slug}`}
                          className="text-decoration-none"
                        >
                          <Card className="h-100 border bg-white transition">
                            <CardContent className="p-3 position-relative">
                              <div className="d-flex align-items-start">
                                <div
                                  className="rounded-3 p-2"
                                  style={{
                                    background: service.gradient,
                                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  <Icon className="text-white" size={20} />
                                </div>
                                <div className="flex-grow-1 ms-3 overflow-hidden">
                                  <h3
                                    className={`fw-semibold mb-1 fs-6 ${
                                      hoveredService === service.slug
                                        ? "text-primary"
                                        : "text-dark"
                                    }`}
                                  >
                                    {service.name}
                                  </h3>
                                  <p className="text-muted fs-xs text-truncate">
                                    {service.description}
                                  </p>
                                </div>
                              </div>
                              <motion.div
                                className="position-absolute inset-0 rounded-3"
                                initial={false}
                                animate={{
                                  opacity:
                                    hoveredService === service.slug ? 1 : 0,
                                }}
                                style={{
                                  background: "rgba(59,130,246, 0.05)",
                                }}
                              />
                            </CardContent>
                          </Card>
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>
                <div className="mt-5 pt-4 border-top text-center">
                  <p className="text-muted mb-3">
                    Can't find what you're looking for? Let's discuss your
                    custom requirements.
                  </p>
                  <Link
                    to="/contact"
                    className="btn btn-primary px-4 py-2 rounded-pill shadow-lg"
                  >
                    Connect Us
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0 }}
              className="d-lg-none mt-3 pb-3 bg-white"
            >
              <nav className="d-flex flex-column p-3">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={`${item.path}`}
                    className="py-2 text-body text-decoration-none"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
                <Link
                  to={"/contact"}
                  variant="default"
                  className="btn d-inline-flex align-items-center justify-center gap-2 text-sm fw-medium transition border-0 rounded
                  btn-primary px-4 py-2 rounded-pill w-100 shadow-lg mt-2"
                >
                  Get Started
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
